import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-responsive-modal";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import AvailableSoon from "@components/Features/available-soon/available-soon";
import { hideModals, setIsSoonVisible } from "@app/store/layoutSlice";
import { AppState } from "@app/store/store";
import { isPrivateRoute } from "@features/auth/AuthCheck";
import "react-responsive-modal/styles.css";

const EnterForm = dynamic(() => import("../auth/enter-form/enter-form"), {
    ssr: false
});
const EnterByEmailForm = dynamic(() => import("../auth/enter-by-email-form/enter-by-email-form"), {
    ssr: false
});
const EnterByPhoneForm = dynamic(() => import("../auth/enter-by-phone-form/enter-by-phone-form"), {
    ssr: false
});
const RegistrationUserTypeStep = dynamic(
    () => import("../auth/registration-user-type-step/registration-user-type-step"),
    { ssr: false }
);
const RegForm = dynamic(() => import("../auth/reg-form/reg-form"), { ssr: false });
const RestorePassForm = dynamic(() => import("../auth/restore-pass-form/restore-pass-form"), {
    ssr: false
});
const FinishRegForm = dynamic(() => import("../auth/finish-reg-form/finish-reg-form"), {
    ssr: false
});
const EnterCodeForm = dynamic(() => import("../auth/enter-code-form/enter-code-form"), {
    ssr: false
});
const CallbackForm = dynamic(() => import("@features/callback-form/callback-form"), { ssr: false });
const SupportForm = dynamic(
    () => import("@features/support/SupportForm").then((mod) => mod.SupportForm),
    { ssr: false }
);
const CompareConflictModal = dynamic(
    () =>
        import("@features/compare/compare-conflict-modal").then((mod) => mod.CompareConflictModal),
    { ssr: false }
);
const UploadBannerForm = dynamic(
    () => import("@features/profile/index").then((mod) => mod.UploadBannerForm),
    { ssr: false }
);

const Modals = () => {
    const dispatch = useDispatch();

    const isPersonal = useSelector((state: AppState) => state.auth.isPersonal);
    const isPersonalSelected = isPersonal !== null && isPersonal !== undefined;
    const isSoonVisible = useSelector((state: AppState) => state.layout.isSoonVisible);
    const enterFormVisible = useSelector((state: AppState) => state.layout.enterFormVisible);
    const regFormVisible = useSelector((state: AppState) => state.layout.registrationFormVisible);

    const {
        enterByEmailVisible,
        emailEnterForm,
        finishRegistrationFormVisible,
        enterCodeFormVisible,
        callbackFormVisible,
        supportFormVisible,
        compareFormVisible,
        bannerUploadVisible
    } = useSelector((state: AppState) => state.layout);
    const restorePassFormVisible = useSelector(
        (state: AppState) => state.layout.restorePassFormVisible
    );
    const user = useSelector((state: AppState) => state.auth.currentUser);

    const showFinishRegStep1 = finishRegistrationFormVisible && !isPersonalSelected && !!user;
    const showFinishRegStep2 = finishRegistrationFormVisible && isPersonalSelected && !!user;
    const isFinishRegVisible = showFinishRegStep1 || showFinishRegStep2;

    const modalVisible =
        enterFormVisible ||
        regFormVisible ||
        restorePassFormVisible ||
        enterByEmailVisible ||
        emailEnterForm ||
        isFinishRegVisible ||
        enterCodeFormVisible ||
        callbackFormVisible ||
        compareFormVisible;

    const customModalVisible = [supportFormVisible, bannerUploadVisible].some(Boolean);

    const pathname = usePathname();
    const isPrivate = isPrivateRoute(pathname || "");
    const router = useRouter();

    const onCloseModal = () => {
        if (isPrivate) {
            if (user) {
                dispatch(hideModals());
            } else {
                router.push("/");
                setTimeout(() => {
                    dispatch(hideModals());
                }, 500);
            }
        } else {
            dispatch(hideModals());
        }
    };

    const isLargeWidth = window.innerWidth > 768 && showFinishRegStep2;

    return (
        <>
            {modalVisible && (
                <Modal
                    styles={{
                        overlay: {
                            backdropFilter: isPrivate ? "blur(5px)" : undefined,
                            // @ts-ignore
                            webKitBackdropFilter: isPrivate ? "blur(5px)" : undefined
                        },
                        modal: {
                            padding: 0,
                            margin: isFinishRegVisible ? 100 : 0,
                            background: "transparent",
                            width: "100%",
                            maxWidth: isLargeWidth ? 800 : 400
                        }
                    }}
                    open={modalVisible}
                    onClose={onCloseModal}
                    center
                    showCloseIcon={false}
                >
                    {enterByEmailVisible && <EnterByEmailForm onClose={onCloseModal} />}
                    {enterFormVisible && <EnterByPhoneForm onClose={onCloseModal} />}
                    {regFormVisible && <RegForm onClose={onCloseModal} />}
                    {restorePassFormVisible && <RestorePassForm onClose={onCloseModal} />}
                    {emailEnterForm && <EnterForm onClose={onCloseModal} />}
                    {enterCodeFormVisible && <EnterCodeForm onClose={onCloseModal} />}
                    {callbackFormVisible && <CallbackForm onClose={onCloseModal} />}
                    {showFinishRegStep1 && <RegistrationUserTypeStep onClose={onCloseModal} />}
                    {showFinishRegStep2 && <FinishRegForm onClose={onCloseModal} />}
                    {compareFormVisible && <CompareConflictModal />}
                </Modal>
            )}
            {customModalVisible && (
                <Modal
                    styles={{
                        overlay: {
                            backdropFilter: isPrivate ? "blur(5px)" : undefined,
                            WebkitBackdropFilter: isPrivate ? "blur(5px)" : undefined
                        },
                        modal: {
                            padding: 0,
                            margin: 0,
                            background: "transparent",
                            width: "auto",
                            overflow: "visible"
                        }
                    }}
                    open={customModalVisible}
                    onClose={onCloseModal}
                    center
                    showCloseIcon={false}
                >
                    {supportFormVisible && <SupportForm onClose={onCloseModal} />}
                    {bannerUploadVisible && <UploadBannerForm onClose={onCloseModal} />}
                </Modal>
            )}
            {isSoonVisible && (
                <Modal
                    styles={{
                        overlay: {
                            backdropFilter: isPrivate ? "blur(5px)" : undefined,
                            WebkitBackdropFilter: isPrivate ? "blur(5px)" : undefined
                        },
                        modal: {
                            padding: 0,
                            margin: 0,
                            background: "transparent",
                            width: "100%",
                            maxWidth: 500
                        }
                    }}
                    open={isSoonVisible}
                    onClose={() => {
                        dispatch(setIsSoonVisible(false));
                    }}
                    center
                    showCloseIcon={false}
                >
                    <AvailableSoon />
                </Modal>
            )}
        </>
    );
};
export default Modals;